/**
 * LoadingStatus — společný stav načítání / zpracování.
 *
 * Telefon i desktop. Živý `status` se doplňuje, jak práce postupuje.
 * Zobrazí se až po ~2 s (`delayMs`), ať krátké akce neblikají.
 * Volitelně progress a kroky. Varianty: page (plocha), block (karta), inline (řádek).
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';

export type LoadingStatusTone = 'loading' | 'working' | 'ready' | 'error';

export interface LoadingStatusStep {
  id: string;
  label: string;
  state?: 'pending' | 'active' | 'done' | 'error';
}

export interface LoadingStatusProps {
  title?: string;
  /** Aktuální fáze — mění se během práce. */
  status?: string;
  detail?: string;
  progress?: number | null;
  steps?: LoadingStatusStep[];
  tone?: LoadingStatusTone;
  variant?: 'page' | 'block' | 'inline';
  /** Neukazovat hned — default 2000 ms. 0 = hned (katalog, test). */
  delayMs?: number;
  className?: string;
}

export function LoadingStatus({
  title,
  status,
  detail,
  progress,
  steps,
  tone = 'loading',
  variant = 'block',
  delayMs = 2000,
  className,
}: LoadingStatusProps) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(delayMs <= 0);
  useEffect(() => {
    if (delayMs <= 0) {
      setVisible(true);
      return;
    }
    setVisible(false);
    const timer = window.setTimeout(() => setVisible(true), delayMs);
    return () => window.clearTimeout(timer);
  }, [delayMs]);
  const heading = title ?? t('common.loading', 'Načítám…');
  const live = status ??
    (tone === 'working'
      ? t('common.waitWorking', 'Zpracovávám…')
      : t('common.loading', 'Načítám…'));
  const pct = typeof progress === 'number'
    ? Math.max(0, Math.min(100, progress))
    : null;
  const busy = tone === 'loading' || tone === 'working';

  if (!visible) {
    return (
      <div
        className={variant === 'page' ? 'acm-wait-page' : undefined}
        role='status'
        aria-live='polite'
        aria-busy={busy}
      />
    );
  }

  const body = (
    <div
      className={cn(
        'acm-wait',
        variant === 'inline' ? 'acm-wait-inline' : 'acm-wait-card acm-glass',
        className,
      )}
      role='status'
      aria-live='polite'
      aria-busy={busy}
    >
      <div className={cn('acm-wait-mark', `is-${tone}`)} aria-hidden>
        <span className='acm-wait-core' />
      </div>
      <div className='acm-wait-copy min-w-0'>
        <div className='acm-wait-title'>{heading}</div>
        <div key={live} className='acm-wait-status'>
          {live}
        </div>
        {detail && <div className='acm-wait-detail'>{detail}</div>}
      </div>
      {(pct !== null || busy) && variant !== 'inline' && (
        <div className={cn('acm-wait-bar', pct === null && 'is-indet')}>
          <i style={pct !== null ? { width: `${pct}%` } : undefined} />
        </div>
      )}
      {steps && steps.length > 0 && variant !== 'inline' && (
        <ol className='acm-wait-steps'>
          {steps.map((step) => (
            <li
              key={step.id}
              className={cn('acm-wait-step', `is-${step.state ?? 'pending'}`)}
            >
              <span className='acm-wait-step-dot' />
              <span className='truncate'>{step.label}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );

  if (variant === 'page') {
    return (
      <div className='acm-wait-page'>
        {body}
      </div>
    );
  }
  return body;
}
